import { DrawerForm, DrawerSizeEnum } from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { InputArea } from '@/components/FormFields/InputArea';
import { InputText } from '@/components/FormFields/InputText';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { usePortainerGroupOptions } from '@/features/portainer/hooks/usePortainerGroupOptions';
import { DrawerFormDataType, FormTypeEnum } from '@/types';

import { useProductionCenterDrawerFormState } from '../../hooks/useProductionCenterDrawerFormState';
import { useProductionCenterTypeOptions } from '../../hooks/useProductionCenterTypeOptions';
import { ProductionCenterType, TProductionCenterForm } from '../../types';

export type TProductionCenterDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<ProductionCenterType>;
  onClose: () => void;
  onSubmit: (data: TProductionCenterForm) => void;
  isMutating: boolean;
};

export const ProductionCenterDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TProductionCenterDrawerFormProps) => {
  const { t } = useTranslation();
  const { form, initialValues } = useProductionCenterDrawerFormState();
  const productionCenterTypeOptions = useProductionCenterTypeOptions();
  const portainerGroupOptions = usePortainerGroupOptions();

  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  const resetForm = () => {
    form.reset(initialValues);
  };

  useEffect(() => {
    if (!data?.data) return;

    const formData: TProductionCenterForm = {
      ...data.data,
      description: data.data.description ?? '',
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset({
        ...formData,
        name: `${data.data.name} (Copia)`,
      });
    }
  }, [data]);

  return (
    <DrawerForm
      title={
        !data?.data
          ? t('Crear nuevo centro de producción')
          : data?.type === FormTypeEnum.UPDATE
            ? 'Editar centro de producción'
            : 'Duplicar centro de producción'
      }
      isOpen={isOpen}
      onCancel={() => {
        onClose();
        resetForm();
      }}
      isMutating={isMutating}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
    >
      <InputText
        form={form}
        name="name"
        required
        label={t('Nombre')}
        placeholder={t('Escribe aquí el nombre corto')}
      />

      <InputArea
        form={form}
        name="description"
        label={t('Descripción')}
        placeholder={t('Escribe aquí tu descripción')}
      />

      <SelectForm
        form={form}
        name="production_center_type"
        required
        label={t('Tipo')}
        placeholder={t('Seleccionar...')}
        options={productionCenterTypeOptions}
      />

      <SelectForm
        form={form}
        name="parameters.group_id"
        required
        label={t('Grupo')}
        placeholder={t('Seleccionar...')}
        options={portainerGroupOptions}
      />
    </DrawerForm>
  );
};
